<template>
  <div class="email-files-page">
    <v-layout row align-center class="email-files-header">
      <v-btn
        outline
        small
        color="white"
        class="email-files-back ma-0 mr-3"
        @click="goBack"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        Back
      </v-btn>
      <div class="email-files-header-copy">
        <div class="email-files-header-title">Manage email files</div>
        <div v-if="accountID" class="email-files-header-sub">
          Account {{ accountID }}
        </div>
      </div>
      <v-spacer></v-spacer>
      <div v-if="!loading && pages.length" class="email-files-header-meta">
        {{ pages.length }}
        {{ pages.length === 1 ? "page" : "pages" }}
      </div>
    </v-layout>

    <div class="email-files-body">
      <v-alert :value="!!error" type="error" class="mb-3">
        {{ error }}
      </v-alert>

      <div v-if="loading" class="email-files-loading">
        <v-progress-circular
          indeterminate
          color="success"
          size="88"
          width="6"
        ></v-progress-circular>
        <div class="email-files-loading-text">Loading files</div>
      </div>

      <v-layout row wrap v-if="!loading">
        <v-flex xs12 md8 class="email-files-grid-col">
          <v-card class="email-files-grid-card">
            <EmailFilesPageGrid
              :pages="pages"
              :selected-keys="selectedKeys"
              @toggle="togglePage"
              @select-all="selectAll"
              @clear="clearSelection"
            />
          </v-card>
        </v-flex>
        <v-flex xs12 md4 class="email-files-panel-col">
          <EmailFilesSavePanel
            :students="uniqueStudents"
            :doc-types="docTypes"
            :selected-pages="selectedPages"
            :student-id.sync="studentId"
            :doc-type-id.sync="docTypeId"
            :file-name.sync="fileName"
            :saving="saving"
            :account-id="accountID"
            @save="save"
          />
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>
<script>
import pdf from "vue-pdf";
import EmailFilesPageGrid from "./EmailFilesPageGrid.vue";
import EmailFilesSavePanel from "./EmailFilesSavePanel.vue";

export default {
  name: "EmailFiles",
  components: {
    EmailFilesPageGrid: EmailFilesPageGrid,
    EmailFilesSavePanel: EmailFilesSavePanel,
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      emailId: 0,
      accountID: "",
      attachments: [],
      pages: [],
      selectedKeys: [],
      students: [],
      docTypes: [],
      studentId: "",
      docTypeId: "",
      fileName: "",
      objectUrls: [],
    };
  },
  computed: {
    uniqueStudents() {
      var seen = {};
      var list = [];

      (this.students || []).forEach(function(student) {
        var id = (student.studentID || "").trim();
        if (!id || seen[id]) {
          return;
        }
        seen[id] = true;
        list.push(student);
      });

      return list;
    },
    selectedPages() {
      const selected = this.selectedKeys;
      // Sort by uploaded order
      //   const result = this.pages.filter((page) => {
      //     return selected.indexOf(page.key) !== -1;
      //   });
      // Sort by selected order
      const result = selected
        .map((key) => this.pages.find((page) => page.key === key))
        .filter(Boolean);
      return result;
    },
  },
  async created() {
    this.emailId = parseInt(this.$route.params.emailId, 10);
    this.accountID = this.$route.query.accountID || "";
    await this.load();
  },
  beforeDestroy() {
    this.revokeObjectUrls();
  },
  methods: {
    goBack() {
      if (this.accountID) {
        this.$router.push({
          name: "Account View",
          params: { id: this.accountID },
          query: { goToEmailsSection: true },
        });
        return;
      }

      this.$router.push({ name: "Emails" });
    },
    revokeObjectUrls() {
      this.objectUrls.forEach(function(url) {
        URL.revokeObjectURL(url);
      });
      this.objectUrls = [];
    },
    fileKind: function(file) {
      var name = ((file && file.fileName) || "").toLowerCase();
      var type = ((file && file.contentType) || "").toLowerCase();
      var ext = name.indexOf(".") !== -1 ? name.split(".").pop() : "";

      if (
        ext === "svg" ||
        ext === "webp" ||
        type.indexOf("image/svg") === 0 ||
        type.indexOf("image/webp") === 0
      ) {
        return "other";
      }
      if (
        type.indexOf("image/") === 0 ||
        ["jpg", "jpeg", "png", "gif", "bmp", "tif", "tiff"].indexOf(ext) !== -1
      ) {
        return "image";
      }
      if (type === "application/pdf" || ext === "pdf") {
        return "pdf";
      }
      return "other";
    },
    getPdfPageCount(src) {
      return new Promise((resolve) => {
        try {
          var task = pdf.createLoadingTask(src);
          task.promise
            .then((doc) => {
              resolve(doc.numPages || 0);
            })
            .catch(() => {
              resolve(0);
            });
        } catch (e) {
          resolve(0);
        }
      });
    },
    async loadObjectUrl(file) {
      try {
        var blob = await this.$http.get(
          "api/emailFiles/getFile?attachmentId=" + file.id,
          { responseType: "blob" }
        );
        var url = URL.createObjectURL(blob);
        this.objectUrls.push(url);
        return url;
      } catch (e) {
        return file.fileURL;
      }
    },
    async load() {
      this.loading = true;
      this.error = "";
      this.revokeObjectUrls();
      this.pages = [];
      this.selectedKeys = [];

      if (!this.emailId) {
        this.error = "Missing email.";
        this.loading = false;
        return;
      }

      try {
        var files = await this.$http.get(
          "api/emails/getAttachments?emailID=" + this.emailId
        );
        this.attachments = files || [];

        if (this.accountID) {
          var students = await this.$http.get(
            "api/accounts/getStudents?accountID=" + this.accountID
          );
          this.students = students || [];
        }

        var types = await this.$http.get("api/emailFiles/documentTypes");
        this.docTypes = types || [];

        var pages = [];
        var index = 1;

        for (var i = 0; i < this.attachments.length; i++) {
          var file = this.attachments[i];
          var kind = this.fileKind(file);
          if (kind !== "pdf" && kind !== "image") {
            continue;
          }

          var src = await this.loadObjectUrl(file);

          var numPages = await this.getPdfPageCount(src);
          if (!numPages) {
            pages.push({
              key: file.id + "-1",
              index: index,
              attachmentId: file.id,
              fileName: file.fileName,
              fileURL: file.fileURL,
              pageNumber: 1,
              src: src,
              kind: kind === "image" ? "image" : "pdf",
            });
            index += 1;
          } else {
            for (var p = 1; p <= numPages; p++) {
              pages.push({
                key: file.id + "-" + p,
                index: index,
                attachmentId: file.id,
                fileName: file.fileName,
                fileURL: file.fileURL,
                pageNumber: p,
                src: src,
                kind: "pdf",
              });
              index += 1;
            }
          }
        }
        this.pages = pages;
      } catch (e) {
        this.error = "Could not load email files.";
      }

      this.loading = false;
    },
    togglePage(page) {
      const next = this.selectedKeys.slice();
      const pos = next.indexOf(page.key);
      if (pos === -1) {
        next.push(page.key);
      } else {
        next.splice(pos, 1);
      }
      this.selectedKeys = next;
    },
    selectAll() {
      this.selectedKeys = this.pages.map(function(page) {
        return page.key;
      });
    },
    clearSelection() {
      this.selectedKeys = [];
    },
    async save() {
      if (!this.accountID) {
        this.$notify({
          group: "notification",
          type: "error",
          text: "This email is not linked to an account.",
        });
        return;
      }

      this.saving = true;
      this.error = "";

      try {
        await this.$http.post("api/emailFiles/save", {
          emailID: this.emailId,
          accountID: this.accountID,
          studentID: this.studentId || null,
          docTypeID: this.docTypeId,
          fileName: this.fileName + ".pdf",
          pages: this.selectedPages.map((page) => {
            return {
              attachmentId: page.attachmentId,
              pageNumber: page.pageNumber,
            };
          }),
        });

        this.$notify({
          group: "notification",
          type: "success",
          text: "Document saved.",
        });
        this.clearSelection();
      } catch (e) {
        this.error =
          e && e.message ? e.message : "Could not save the document.";
      }

      this.saving = false;
    },
  },
};
</script>
<style>
.email-files-page {
  padding: 10px 16px;
  margin: 0 auto;
}

.email-files-header {
  background: #4caf50;
  color: #fff;
  padding: 10px 16px;
  min-height: 56px;
}

.email-files-back {
  border-color: rgba(255, 255, 255, 0.7) !important;
  color: #fff !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.email-files-back:hover {
  background: rgba(255, 255, 255, 0.12) !important;
}

.email-files-header-copy {
  min-width: 0;
}

.email-files-header-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}

.email-files-header-sub,
.email-files-header-meta {
  font-size: 12px;
  opacity: 0.9;
}

.email-files-header-meta {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 4px 10px;
  font-weight: 600;
}

.email-files-body {
  padding: 16px;
}

.email-files-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.email-files-loading-text {
  margin-top: 12px;
  color: #757575;
  font-size: 13px;
  font-weight: 500;
}

.email-files-grid-col {
  padding-right: 8px;
}

.email-files-panel-col {
  padding-left: 8px;
}

.email-files-grid-card {
  padding: 12px 12px 8px;
  border-radius: 8px !important;
}

@media (max-width: 960px) {
  .email-files-grid-col,
  .email-files-panel-col {
    padding-left: 0;
    padding-right: 0;
  }

  .email-files-panel-col {
    margin-top: 12px;
  }
}
</style>
